import React from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import './WorkflowSection.css';

const STEPS = [
  {
    number: '01',
    title: 'Patient Registration',
    desc: 'Capture patient demographics, history, and insurance details in seconds.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Appointment Booking',
    desc: 'Walk-in or online — slot the patient with the right doctor instantly.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <rect x="3" y="4" width="18" height="18"/><line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Doctor Consultation',
    desc: 'Doctor reviews complete patient history in one screen, no paper needed.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Prescription Creation',
    desc: 'Generate smart digital prescriptions via voice, template, or manual entry.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
  },
  {
    number: '05',
    title: 'Pharmacy Processing',
    desc: 'Prescription auto-links to pharmacy. Dispense, bill, and update stock in real time.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <path d="M18.5 2h-13A2.5 2.5 0 0 0 3 4.5v1A2.5 2.5 0 0 0 5.5 8H6v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8h.5A2.5 2.5 0 0 0 21 5.5v-1A2.5 2.5 0 0 0 18.5 2z"/>
        <line x1="10" y1="12" x2="14" y2="12"/><line x1="12" y1="10" x2="12" y2="14"/>
      </svg>
    ),
  },
  {
    number: '06',
    title: 'Follow-up Scheduling',
    desc: 'Auto-schedule follow-ups and send reminders, keeping the care cycle complete.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    ),
  },
];

const WorkflowSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="workflow section section-light" id="workflow" ref={ref}>
      <div className="container">
        <div className={`section-header centered reveal${isVisible ? ' visible' : ''}`}>
          <span className="section-tag">Patient Journey</span>
          <h2 className="section-title">End-to-End Clinical Workflow</h2>
          <p className="section-subtitle centered">
            Follow a patient from the moment they walk in to the completion of their care cycle — every step digitized and connected.
          </p>
        </div>

        <div className="workflow__track">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.number}>
              <div className={`workflow__step reveal delay-${i + 1}${isVisible ? ' visible' : ''}`}>
                <div className="workflow__step-number">{step.number}</div>
                <div className="workflow__step-icon-wrap">
                  {step.icon}
                </div>
                <div className="workflow__step-title">{step.title}</div>
                <div className="workflow__step-desc">{step.desc}</div>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`workflow__arrow${isVisible ? ' workflow__arrow--animate' : ''}`}>
                  <div className="arrow__line" style={{ animationDelay: `${0.2 + i * 0.15}s` }} />
                  <div className="arrow__head" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Mini timeline strip */}
        <div className={`workflow__cta-strip reveal delay-6${isVisible ? ' visible' : ''}`}>
          <div className="workflow__cta-content">
            <span className="workflow__cta-text">
              The entire journey — registration to follow-up — takes less than
            </span>
            <span className="workflow__cta-highlight"> 10 minutes</span>
            <span className="workflow__cta-text"> per patient with Tecnivorns HMS.</span>
          </div>
          {/* <a href="#demo" className="btn-primary">See It Live</a> */}
        </div>
      </div>
    </section>
  );
};

export default WorkflowSection;
